'use strict';

var utils = require('../utils');
var AxiosError = require('../core/AxiosError');
var envFormData = require('../env/classes/FormData');

function isVisitable(thing) {
  return utils.isPlainObject(thing) || utils.isArray(thing);
}

function removeBrackets(key) {
  return utils.endsWith(key, '[]') ? key.slice(0, -2) : key;
}

function renderKey(path, key, dots) {
  if (!path) return key;
  return path.concat(key).map(function each(token, i) {
    // eslint-disable-next-line no-param-reassign
    token = removeBrackets(token);
    return !dots && i ? '[' + token + ']' : token;
  }).join(dots ? '.' : '');
}

function isFlatArray(arr) {
  return utils.isArray(arr) && !arr.some(isVisitable);
}

var predicates = utils.toFlatObject(utils, {}, null, function filter(prop) {
  return /^is[A-Z]/.test(prop);
});

function isSpecCompliant(thing) {
  return thing && utils.isFunction(thing.append) && thing[Symbol.toStringTag] === 'FormData' && thing[Symbol.iterator];
}

var UNSAFE_OPTION_KEYS = ['__proto__', 'constructor', 'prototype'];

// Materialises the serializer options onto a null-prototype object. Reading the
// options from a plain object let a polluted Object.prototype supply a visitor,
// a depth limit, a Blob implementation or key-format flags that the caller never
// asked for, and also let a polluted key mask an option the caller did set.
// Only own, defined properties of the caller's options are carried over.
function toFormDataOptions(source) {
  var options = Object.create(null);

  options.metaTokens = true;
  options.dots = false;
  options.indexes = false;

  // eslint-disable-next-line no-eq-null,eqeqeq
  if (source == null) {
    return options;
  }

  var names = Object.getOwnPropertyNames(source);

  for (var i = 0; i < names.length; i++) {
    var name = names[i];

    if (UNSAFE_OPTION_KEYS.indexOf(name) !== -1 || utils.isUndefined(source[name])) {
      continue;
    }

    options[name] = source[name];
  }

  return options;
}

/**
 * Convert a data object to FormData
 * @param {Object} obj
 * @param {?Object} [formData]
 * @param {?Object} [options]
 * @param {Function} [options.visitor]
 * @param {Boolean} [options.metaTokens = true]
 * @param {Boolean} [options.dots = false]
 * @param {?Boolean} [options.indexes = false]
 * @returns {Object}
 **/

function toFormData(obj, formData, options) {
  if (!utils.isObject(obj)) {
    throw new TypeError('target must be an object');
  }

  // eslint-disable-next-line no-param-reassign
  formData = formData || new (envFormData || FormData)();

  // eslint-disable-next-line no-param-reassign
  options = toFormDataOptions(options);

  var metaTokens = options.metaTokens;
  // eslint-disable-next-line no-use-before-define
  var visitor = options.visitor || defaultVisitor;
  var dots = options.dots;
  var indexes = options.indexes;
  var _Blob = options.Blob || typeof Blob !== 'undefined' && Blob;
  var maxDepth = options.maxDepth === undefined ? 100 : options.maxDepth;
  var useBlob = _Blob && isSpecCompliant(formData);

  if (!utils.isFunction(visitor)) {
    throw new TypeError('visitor must be a function');
  }

  function convertValue(value) {
    if (value === null) return '';

    if (utils.isDate(value)) {
      return value.toISOString();
    }

    if (!useBlob && utils.isBlob(value)) {
      throw new AxiosError('Blob is not supported. Use a Buffer instead.');
    }

    if (utils.isArrayBuffer(value) || utils.isTypedArray(value)) {
      return useBlob && typeof Blob === 'function' ? new Blob([value]) : Buffer.from(value);
    }

    return value;
  }

  var stack = [];

  function assertValueDepth(value, depth) {
    if (depth > maxDepth) {
      throw new AxiosError(
        'Maximum object depth of ' + maxDepth + ' exceeded (got ' + depth + ' levels)',
        AxiosError.ERR_FORM_DATA_DEPTH_EXCEEDED
      );
    }

    if (!utils.isObject(value) || stack.indexOf(value) !== -1) {
      return;
    }

    stack.push(value);

    utils.forEach(value, function each(el) {
      assertValueDepth(el, depth + 1);
    });

    stack.pop();
  }

  /**
   *
   * @param {*} value
   * @param {String|Number} key
   * @param {Array<String|Number>} path
   * @this {FormData}
   * @returns {boolean} return true to visit the each prop of the value recursively
   */
  function defaultVisitor(value, key, path) {
    var arr = value;

    if (value && !path && typeof value === 'object') {
      if (utils.endsWith(key, '{}')) {
        // eslint-disable-next-line no-param-reassign
        key = metaTokens ? key : key.slice(0, -2);
        assertValueDepth(value, 1);
        // eslint-disable-next-line no-param-reassign
        value = JSON.stringify(value);
      } else if (
        (utils.isArray(value) && isFlatArray(value)) ||
        (utils.isFileList(value) || utils.endsWith(key, '[]') && (arr = utils.toArray(value))
        )) {
        // eslint-disable-next-line no-param-reassign
        key = removeBrackets(key);

        arr.forEach(function each(el, index) {
          !(utils.isUndefined(el) || el === null) && formData.append(
            // eslint-disable-next-line no-nested-ternary
            indexes === true ? renderKey([key], index, dots) : (indexes === null ? key : key + '[]'),
            convertValue(el)
          );
        });
        return false;
      }
    }

    if (isVisitable(value)) {
      return true;
    }

    formData.append(renderKey(path, key, dots), convertValue(value));

    return false;
  }

  var exposedHelpers = Object.assign(predicates, {
    defaultVisitor: defaultVisitor,
    convertValue: convertValue,
    isVisitable: isVisitable
  });

  function build(value, path, depth) {
    if (utils.isUndefined(value)) return;

    // eslint-disable-next-line no-param-reassign
    depth = depth || 0;

    if (depth > maxDepth) {
      throw new AxiosError(
        'Maximum object depth of ' + maxDepth + ' exceeded (got ' + depth + ' levels)',
        AxiosError.ERR_FORM_DATA_DEPTH_EXCEEDED
      );
    }

    if (stack.indexOf(value) !== -1) {
      throw Error('Circular reference detected in ' + path.join('.'));
    }

    stack.push(value);

    utils.forEach(value, function each(el, key) {
      var result = !(utils.isUndefined(el) || el === null) && visitor.call(
        formData, el, utils.isString(key) ? key.trim() : key, path, exposedHelpers
      );

      if (result === true) {
        build(el, path ? path.concat(key) : [key], depth + 1);
      }
    });

    stack.pop();
  }

  if (!utils.isObject(obj)) {
    throw new TypeError('data must be an object');
  }

  build(obj, null, 0);

  return formData;
}

module.exports = toFormData;
